import {Component, Input, OnInit} from '@angular/core';
import {FormGroup} from '@angular/forms';
import {NgxSpinnerService} from 'ngx-spinner';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {IResizeImageOptions, ImageManipulationService} from '../../core/image-manipulation.service';
import {OcrService, TextDetection} from '../../core/ocr.service';
import {Card, ImageAsset, ImageAssetFactory, ImageAssetType} from '../../domain/card';
import {OcrAction, OcrActionRegex} from '../../domain/card-actions/ocr.action';

@Component({
  selector: 'crds-ocr-form',
  templateUrl: './ocr-form.component.html',
})
export class OcrFormComponent implements OnInit {

  @Input() form: FormGroup;
  @Input() card: Card<OcrAction>;
  action: OcrAction;
  description: SafeHtml;
  images: ImageAsset[];
  imageAssetType = ImageAssetType;

  constructor(private spinnerService: NgxSpinnerService,
              private ocrService: OcrService,
              private sanitizer: DomSanitizer,
              private imageService: ImageManipulationService,
              private imageAssetFactory: ImageAssetFactory) {
  }

  ngOnInit(): void {
    this.action = this.card.action;
    this.images = this.imageAssetFactory.create(this.card.image, this.form);
    this.description = this.sanitizer.bypassSecurityTrustHtml(this.card.description);
  }

  setFromOcr(event: any) {
    this.spinnerService.show();
    this.clearOcrTarget();
    const file: File = event.target.files[0];
    const targetFormControl = this.form.get(this.action.targetField);
    let tempImage: any;
    let orientationTemp = 1;

    setTimeout(() => {
      this.spinnerService.hide();
    }, 10000);

    return this.imageService.resetOrientation(file)

      .then((reorientationResult: { result: string, orientation: number }) => {
        orientationTemp = reorientationResult.orientation;
        tempImage = reorientationResult.result;
        const resizeForOcrOptions: IResizeImageOptions = {
          maxSize: 2000,
          b64: tempImage
        };
        return this.imageService.resizeImage(resizeForOcrOptions);
      })
      .then((resizedImage) => {
        tempImage = resizedImage;
        return this.ocrService.recognise(tempImage);
      })
      .then((results: TextDetection[]) => {
        const filteredResult = this.filterResults(results);
        this.patchOcrTarget(filteredResult);
        if (filteredResult === null) {
          return Promise.resolve(tempImage);
        }
        return this.imageService.cropImage(tempImage, filteredResult);
      })
      .then((croppedImage: string) => {
        targetFormControl.patchValue(croppedImage);
        this.spinnerService.hide();
        this.resetFileInput();
      })
      .catch(console.error);
  }

  private filterResults(results: TextDetection[]): TextDetection | null {
    let returnedResult: TextDetection = null;

    this.action.regexs.forEach((regex: OcrActionRegex) => {

      switch (regex.type) {
        case 'standard':
          const standardFilteredResults = results.filter((result) => result.Type === 'WORD');
          const standardResult = this.standardFilter(standardFilteredResults, regex);
          if (standardResult) {
            returnedResult = standardResult;
          }
          break;
        case 'split':
          const splitFilteredResults = results.filter((result) => result.Type === 'LINE');
          const splitResult = this.splitFilter(splitFilteredResults, regex);
          if (splitResult) {
            returnedResult = splitResult;
          }
          break;
        default:
          returnedResult = null;
      }
    });

    return returnedResult;
  }

  /**
   * Attempt to find the index of the first pattern in the results then try to match the subsequent
   * results against the subsequent patterns.
   * @returns TextDetection | null
   * @param results
   * @param regex
   */
  private splitFilter(results: TextDetection[], regex: OcrActionRegex): TextDetection | null {
    let match: TextDetection = null;

    results.forEach((result) => {
      if (result.DetectedText.match(regex.patterns[0]) !== null) {
        match = result;
      }
    });

    return match;
  }

  private standardFilter(results: TextDetection[], regex: OcrActionRegex): TextDetection | null {
    const filteredResults = results.filter(
      (result) => result.DetectedText.match(regex.patterns[0]) !== null
    );
    return filteredResults.length ? filteredResults[0] : null;
  }

  private patchOcrTarget(result: TextDetection) {
    const target = this.form.get(this.action.targetOcr);
    if (result !== null) {
      const match = result.DetectedText.match(/(([1-2][0-9]|3[0-2]) ?[0-9]{4} ?[0-9]{4} ?[0-9]{3}).*/);
      target.patchValue(match.length ? match[1].replace(/ /g, '') : '');
    } else {
      target.patchValue('');
    }
  }

  private clearOcrTarget() {
    this.form.get(this.action.targetOcr).reset('');
  }

  clickFile() {
    document.getElementById('ocr').click();
  }

  resetFileInput() {
    const input = document.getElementById('ocr');
    input['value'] = '';

    if (!/safari/i.test(navigator.userAgent)) {
      input['type'] = '';
      input['type'] = 'file';
    }
  }
}
